import { useState, type FormEvent } from "react";
import { Button, Field, DialogCancel } from "@cntrl/ui";
import { Modal } from "../../releases/Modal.js";
import { Segmented } from "../adcreator/Segmented.js";
import { CharCounter } from "../adcreator/CharCounter.js";
import { formErrorMessage } from "../../../app/hooks/apiError.js";
import { useCapability } from "../../../app/bandContext.js";
import { useComposeBroadcast, type SendBroadcastRequest } from "../../../app/hooks/useBroadcasts.js";
import { useFans } from "../../../app/hooks/useFans.js";
import styles from "./FanForm.module.scss";

type Channel = "email" | "sms";
type Audience = "all" | "subscribed" | "sms";
const BODY_MAX = 2000;

/**
 * The broadcast composer — a routed modal (`/marketing/fans/broadcasts/new`), deep-linkable. v1 is
 * LOG-ONLY: a prominent banner says so, and Compose LOGS the broadcast without sending. Never optimistic:
 * explicit pending, server-confirm (the history repaints via the broadcast-changed signal + the invalidate).
 * Opted-out fans are suppressed from every audience by construction (nothing sends).
 */
export function BroadcastComposerModal({ onClose, onLogged }: { onClose: () => void; onLogged: () => void }) {
  const canManage = useCapability("fans:manage");
  const compose = useComposeBroadcast();
  const fans = useFans();

  const [channel, setChannel] = useState<Channel>("email");
  const [template, setTemplate] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<Audience>("subscribed");
  const [error, setError] = useState<string | null>(null);

  // Deep-link guard (a fans-denied seat could hit /marketing/fans/broadcasts/new directly). The API also 403s,
  // but don't render the composer over the "not available" screen. Called AFTER all hooks (rules of hooks).
  if (!canManage) return null;

  const stats = fans.data?.stats;
  const reach = stats
    ? audience === "sms"
      ? stats.smsReach ?? 0
      : audience === "subscribed"
        ? stats.subscribed ?? 0
        : (stats.total ?? 0) - (stats.optOuts ?? 0)
    : null;

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const req: SendBroadcastRequest = {
      channel,
      template: template || null,
      subject: channel === "email" ? subject || null : null,
      body: body || null,
      audienceKind: audience,
      releaseId: null,
    };
    compose.mutate(req, {
      onSuccess: onLogged,
      onError: (err) => setError(formErrorMessage(err, "Couldn't save the broadcast")),
    });
  }

  return (
    <Modal title="Compose broadcast" size="lg" onClose={onClose} dirty="auto">
      <form className={styles.form} onSubmit={submit}>
        <p className={styles.banner}>
          <strong>Log-only:</strong> this records the broadcast but doesn&apos;t send it. Email &amp; SMS delivery ship later.
        </p>

        <div className={styles.row}>
          <span className={styles.label}>Channel</span>
          <Segmented<Channel>
            ariaLabel="Channel"
            value={channel}
            onChange={setChannel}
            options={[{ value: "email", label: "Email" }, { value: "sms", label: "SMS" }]}
          />
        </div>

        <Field label="Template" value={template} onChange={(e) => setTemplate(e.currentTarget.value)} hint="Optional label for this broadcast" />

        {channel === "email" && (
          <Field label="Subject" value={subject} onChange={(e) => setSubject(e.currentTarget.value)} />
        )}

        <div className={styles.row}>
          <span className={styles.label}>Message <CharCounter count={body.length} max={BODY_MAX} /></span>
          <textarea className={styles.textarea} value={body} maxLength={BODY_MAX} onChange={(e) => setBody(e.currentTarget.value)} />
        </div>

        <div className={styles.row}>
          <span className={styles.label}>Audience</span>
          <Segmented<Audience>
            ariaLabel="Audience"
            value={audience}
            onChange={setAudience}
            options={[
              { value: "subscribed", label: "Subscribed" },
              { value: "sms", label: "SMS opt-ins" },
              { value: "all", label: "Everyone" },
            ]}
          />
          <p className={styles.note}>
            {fans.isError
              ? "Reach unavailable."
              : reach == null
                ? "Reach calculated once fans load."
                : `Would reach ~${reach} ${reach === 1 ? "fan" : "fans"} (opted-out fans excluded).`}
          </p>
        </div>

        {error && <p className={styles.formError} role="alert">{error}</p>}
        <div className={styles.actionsEnd}>
          <DialogCancel />
          <Button type="submit" variant="primary" loading={compose.isPending}>Log broadcast</Button>
        </div>
      </form>
    </Modal>
  );
}
